import React, { useState, useEffect } from 'react';
import { Phone, MapPin, Menu, X } from 'lucide-react';
import { businessInfo } from '../mockData';
import { Button } from './ui/button';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-white shadow-md py-3' : 'bg-white/95 backdrop-blur-sm py-4'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <div className="flex-shrink-0">
            <div className="flex items-center space-x-3">
              {/* White Car Icon Badge */}
              <div className="relative">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-blue-600 to-blue-800 rounded-xl flex items-center justify-center shadow-lg transform hover:scale-105 transition-transform">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8 sm:w-9 sm:h-9 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/>
                  </svg>
                </div>
                <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center shadow-md">
                  <span className="text-white text-xs font-bold">✓</span>
                </div>
              </div>
              
              {/* Text with full-width underline */}
              <div className="flex flex-col">
                <h1 className="text-xl sm:text-2xl lg:text-3xl leading-none" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  <span className="bg-gradient-to-r from-blue-600 via-blue-700 to-blue-900 text-transparent bg-clip-text font-bold" style={{ letterSpacing: '0.02em' }}>
                    M.A.
                  </span>
                  <span className="ml-1 text-gray-800" style={{ letterSpacing: '0.02em', fontWeight: 300, fontStyle: 'italic' }}>
                    Zulassungsdienst
                  </span>
                </h1>
                <div className="h-1 w-full bg-gradient-to-r from-blue-600 to-green-500 rounded-full mt-1"></div>
              </div>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            <button onClick={() => scrollToSection('services')} className="text-gray-700 hover:text-blue-600 transition-colors font-medium">
              Leistungen
            </button>
            <button onClick={() => scrollToSection('why-us')} className="text-gray-700 hover:text-blue-600 transition-colors font-medium">
              Vorteile
            </button>
            <button onClick={() => scrollToSection('reviews')} className="text-gray-700 hover:text-blue-600 transition-colors font-medium">
              Bewertungen
            </button>
            <button onClick={() => scrollToSection('contact')} className="text-gray-700 hover:text-blue-600 transition-colors font-medium">
              Kontakt
            </button>
          </nav>

          {/* Contact Info */}
          <div className="hidden md:flex items-center space-x-4">
            <a href={`tel:${businessInfo.phone}`} className="flex items-center space-x-2 text-gray-700 hover:text-blue-600 transition-colors">
              <Phone className="w-4 h-4" />
              <span className="font-semibold">{businessInfo.phone}</span>
            </a>
            <Button 
              onClick={() => scrollToSection('contact-form')}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              Anfrage starten
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 text-gray-700 hover:text-blue-600 transition-colors"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 border-t border-gray-200 pt-4">
            <nav className="flex flex-col space-y-3">
              <button onClick={() => scrollToSection('services')} className="text-left text-gray-700 hover:text-blue-600 transition-colors font-medium py-2">
                Leistungen
              </button>
              <button onClick={() => scrollToSection('why-us')} className="text-left text-gray-700 hover:text-blue-600 transition-colors font-medium py-2">
                Vorteile
              </button>
              <button onClick={() => scrollToSection('reviews')} className="text-left text-gray-700 hover:text-blue-600 transition-colors font-medium py-2">
                Bewertungen
              </button>
              <button onClick={() => scrollToSection('contact')} className="text-left text-gray-700 hover:text-blue-600 transition-colors font-medium py-2">
                Kontakt
              </button>
              <a href={`tel:${businessInfo.phone}`} className="flex items-center space-x-2 text-gray-700 hover:text-blue-600 transition-colors py-2">
                <Phone className="w-4 h-4" />
                <span className="font-semibold">{businessInfo.phone}</span>
              </a>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};
